import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, History, Loader2, Mail, Plus, RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/components/PartnerCode";

const STATUS = {
  pending_email: ["Pending Email Assignment", "bg-[#F7EEDD] text-[#8A6420]"],
  ready: ["Ready to Redeem", "bg-[#E8F0F7] text-[#2F5D86]"],
  active: ["Active", "bg-[#E7F1EA] text-[#2E6B4F]"],
  expired: ["Expired", "bg-[#F1ECE6] text-[#6E5549]"],
  revoked: ["Revoked", "bg-[#F9ECEC] text-[#8C2A2A]"],
};
const input = "h-10 w-full rounded-xl border border-[#E6DBCF] bg-white px-3 text-sm text-[#2C1810] outline-none focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30";
const lbl = "text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]";
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function useCoupons() {
  return useQuery({ queryKey: ["partner-coupons"], queryFn: async () => (await api.get("/admin/partner-coupons")).data });
}

function StatusBadge({ c }) {
  const [l, cls] = STATUS[c.status];
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`} data-testid={`coupon-status-${c.code}`}>{l}{c.status === "ready" && c.pending_verification ? " · awaiting email verification" : ""}</span>;
}

function CouponCard({ c, refresh }) {
  const [email, setEmail] = useState(c.email || "");
  const [end, setEnd] = useState(c.end_date);
  const [busy, setBusy] = useState(false);
  const dirty = (email.trim().toLowerCase() || null) !== (c.email || null) || end !== c.end_date;
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    const body = {};
    if ((email.trim().toLowerCase() || null) !== (c.email || null)) body.email = email.trim();
    if (end !== c.end_date) body.end_date = end;
    const shift = body.end_date && c.status === "active" ? `\n\nThis partner's live access will change to end ${fmtDate(end)} at 11:59:59 p.m. Pacific.` : "";
    if (shift && !window.confirm(`Change ${c.partner_name}'s access end date?${shift}`)) return;
    run(() => api.patch(`/admin/partner-coupons/${c.id}`, body), "Coupon updated");
  };
  const copy = async () => {
    await navigator.clipboard.writeText(c.code);
    toast.success(`Copied ${c.code}`);
  };
  const locked = c.status === "revoked";
  const unused = !c.redeemed_at && !locked;
  return (
    <div className="rounded-2xl border border-[#E6DBCF] bg-white/70 p-5" data-testid={`coupon-card-${c.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-serif text-xl font-semibold text-[#2C1810]" data-testid={`coupon-name-${c.code}`}>{c.partner_name}</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="rounded-lg bg-[#F6F1EA] px-2.5 py-1 font-mono text-sm tracking-wider text-[#2C1810]" data-testid={`coupon-code-${c.code}`}>{c.code}</code>
            <button onClick={copy} aria-label={`Copy ${c.code}`} data-testid={`coupon-copy-${c.code}`} className="rounded-full p-1.5 text-[#6E5549] hover:bg-[#F6F1EA] hover:text-[#2C1810]"><Copy size={14} /></button>
          </div>
        </div>
        <StatusBadge c={c} />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1.4fr_1fr_auto] sm:items-end">
        <label className="block"><span className={lbl}>Authorized email</span>
          <input type="email" value={email} disabled={locked || !!c.redeemed_at} onChange={(e) => setEmail(e.target.value)} placeholder="Assign partner's account email" className={`${input} mt-1 disabled:bg-[#F6F1EA]`} data-testid={`coupon-email-${c.code}`} /></label>
        <label className="block"><span className={lbl}>Access through (PT)</span>
          <input type="date" value={end} disabled={locked} onChange={(e) => setEnd(e.target.value)} className={`${input} mt-1 disabled:bg-[#F6F1EA]`} data-testid={`coupon-end-${c.code}`} /></label>
        <button disabled={!dirty || busy} onClick={save} data-testid={`coupon-save-${c.code}`} className="h-10 rounded-full bg-[#2C1810] px-5 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-30">Save</button>
      </div>
      <dl className="mt-4 grid gap-y-1 text-xs text-[#4A3B32]">
        <div><dt className="inline text-[#A88B7D]">Access ends: </dt><dd className="inline" data-testid={`coupon-end-display-${c.code}`}>{c.end_display}, 11:59:59 p.m. PT</dd></div>
        <div><dt className="inline text-[#A88B7D]">Redeemed: </dt><dd className="inline" data-testid={`coupon-redeemed-${c.code}`}>{c.redeemed_at ? `${when(c.redeemed_at)}${c.redeemed_by_email ? ` · ${c.redeemed_by_email}` : ""}` : "Not yet"}</dd></div>
        {c.email && !c.redeemed_at && !locked && (
          <div><dt className="inline text-[#A88B7D]">Welcome email: </dt><dd className="inline" data-testid={`coupon-welcome-${c.code}`}>{c.welcome_sent_at ? `Sent ${when(c.welcome_sent_at)}` : c.welcome_error ? <span className="text-[#B42318]">Failed — {c.welcome_error}</span> : "Not sent"}</dd></div>
        )}
        {c.expiry_notice_sent_at && <div><dt className="inline text-[#A88B7D]">Expiry heads-up: </dt><dd className="inline" data-testid={`coupon-expiry-notice-${c.code}`}>Sent {when(c.expiry_notice_sent_at)}</dd></div>}
        {c.previous_codes?.length > 0 && <div><dt className="inline text-[#A88B7D]">Replaced codes (no longer work): </dt><dd className="inline font-mono">{c.previous_codes.join(", ")}</dd></div>}
      </dl>
      {c.account && unused && (
        <p className={`mt-3 rounded-xl px-3 py-2 text-xs ${["paid", "trialing", "past_due"].includes(c.account.handling.case) ? "bg-[#FFF4E5] text-[#8A4B12]" : "bg-[#F6F1EA] text-[#4A3B32]"}`} data-testid={`coupon-handling-${c.code}`}>
          <b>On redemption:</b> {c.account.handling.text}
        </p>
      )}
      {!locked && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-[#EFE6DA] pt-3">
          {unused && (
            <button disabled={busy} onClick={() => window.confirm(`Replace ${c.code} with a new code? The old code will stop working.`) && run(() => api.post(`/admin/partner-coupons/${c.id}/replace-code`, {}), "New code created")}
              data-testid={`coupon-replace-${c.code}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E6DBCF] px-3 py-1.5 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA]"><RefreshCw size={12} /> Replace code</button>
          )}
          {unused && c.email && c.status === "ready" && (
            <button disabled={busy} onClick={() => run(() => api.post(`/admin/partner-coupons/${c.id}/send-welcome`), `Welcome email sent to ${c.email}`)}
              data-testid={`coupon-send-welcome-${c.code}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E6DBCF] px-3 py-1.5 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA]"><Mail size={12} /> {c.welcome_sent_at ? "Resend welcome email" : "Send welcome email"}</button>
          )}
          <button disabled={busy} onClick={() => window.confirm(`Revoke ${c.code} for ${c.partner_name}?${c.status === "active" ? " Their partner access ends immediately (their kit and account stay saved)." : ""}`) && run(() => api.post(`/admin/partner-coupons/${c.id}/revoke`), "Coupon revoked")}
            data-testid={`coupon-revoke-${c.code}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#F0C9C4] px-3 py-1.5 text-xs font-semibold text-[#B42318] hover:bg-[#F9ECEC]"><ShieldOff size={12} /> Revoke</button>
          {busy && <Loader2 size={14} className="animate-spin self-center text-[#C5A059]" />}
        </div>
      )}
    </div>
  );
}

function CreateCoupon({ refresh }) {
  const empty = { partner_name: "", end_date: "", code: "", email: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partner-coupons", f);
      toast.success(`Created ${data.code}`);
      setF(empty);
      await refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-dashed border-[#D9C3A0] bg-[#FFFBF2] p-5" data-testid="coupon-create-form">
      <p className="font-serif text-lg font-semibold text-[#2C1810]">Create a partner coupon</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label><span className={lbl}>Partner name *</span><input required value={f.partner_name} onChange={set("partner_name")} className={`${input} mt-1`} data-testid="coupon-create-name" /></label>
        <label><span className={lbl}>Access through *</span><input required type="date" value={f.end_date} onChange={set("end_date")} className={`${input} mt-1`} data-testid="coupon-create-end" /></label>
        <label><span className={lbl}>Code (auto if blank)</span><input value={f.code} onChange={set("code")} placeholder="MBL-NAME-2027" className={`${input} mt-1 font-mono`} data-testid="coupon-create-code" /></label>
        <label><span className={lbl}>Authorized email</span><input type="email" value={f.email} onChange={set("email")} className={`${input} mt-1`} data-testid="coupon-create-email" /></label>
      </div>
      <button disabled={busy} data-testid="coupon-create-submit" className="mt-4 inline-flex h-10 items-center gap-2 rounded-full bg-[#C5A059] px-5 text-sm font-semibold text-[#2C1810] hover:bg-[#d4b273] disabled:opacity-50"><Plus size={15} /> Create coupon</button>
    </form>
  );
}

function AuditLog() {
  const { data = [] } = useQuery({ queryKey: ["coupon-audit"], queryFn: async () => (await api.get("/admin/partner-coupons/audit")).data });
  return (
    <div className="rounded-2xl border border-[#E6DBCF] bg-white/70 p-5" data-testid="coupon-audit">
      <p className="flex items-center gap-2 font-serif text-lg font-semibold text-[#2C1810]"><History size={17} /> Change history</p>
      <ul className="mt-3 max-h-96 divide-y divide-[#EFE6DA] overflow-y-auto text-sm">
        {data.map((a) => (
          <li key={`${a.at}-${a.code}-${a.action}`} className="py-2.5" data-testid="coupon-audit-row">
            <p className="text-[#2C1810]"><b>{a.partner_name}</b> · <span className="font-mono text-xs">{a.code}</span> · <span className="uppercase tracking-wider text-[10px] text-[#9C6218]">{a.action.replace("_", " ")}</span></p>
            <p className="text-xs text-[#6E5549]">{a.details}</p>
            <p className="text-[10px] text-[#A88B7D]">{when(a.at)} · {a.by}</p>
          </li>
        ))}
        {!data.length && <li className="py-3 text-xs text-[#A88B7D]">No changes yet.</li>}
      </ul>
    </div>
  );
}

export function PartnerCouponsPanel() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useCoupons();
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["partner-coupons"] }), qc.invalidateQueries({ queryKey: ["coupon-audit"] })]);
  const needEmail = data.filter((c) => c.status === "pending_email");
  return (
    <div className="space-y-6" data-testid="admin-partner-coupons">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-[#2C1810]">Partner Access & Coupons</h2>
        <p className="mt-1 text-sm text-[#6E5549]">MooreBeautyLove 1-Year PMU Accelerator partners get complimentary access through a fixed date (11:59:59 p.m. Pacific). Codes only work for the assigned, verified email, once. Assigning an email automatically sends the partner their code and sign-up link, and partners get a heads-up two weeks before access ends. No card is collected and nothing is charged when access ends.</p>
        {needEmail.length > 0 && <p className="mt-3 rounded-xl bg-[#F7EEDD] px-4 py-2.5 text-sm text-[#6B5434]" data-testid="coupon-need-email">{needEmail.length} coupon{needEmail.length === 1 ? "" : "s"} still need an email assigned: {needEmail.map((c) => c.partner_name).join(", ")}.</p>}
      </div>
      <CreateCoupon refresh={refresh} />
      {isLoading ? <p className="text-sm text-[#6E5549]">Loading coupons…</p> : (
        <div className="grid gap-4 xl:grid-cols-2">{data.map((c) => <CouponCard key={`${c.id}-${c.code}-${c.email}-${c.end_date}`} c={c} refresh={refresh} />)}</div>
      )}
      <AuditLog />
    </div>
  );
}
